import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTracks } from "@/context/TrackContext";

const FIELDS = [
  { key: "name", label: "Full Name", required: true, placeholder: "Jane Smith" },
  { key: "department", label: "Department", placeholder: "Sales" },
  { key: "position", label: "Position", placeholder: "Property Consultant" },
  { key: "contact", label: "Contact (Landline or Mobile)", placeholder: "+61 400 000 000" },
];

export const UserForm = ({ initial, onClose, onSaved }) => {
  const { user: me } = useAuth();
  const { tracks } = useTracks();
  const isSuper = me?.role === "super_admin";
  const editing = Boolean(initial);
  const [form, setForm] = useState({
    name: initial?.name || "",
    department: initial?.department || "",
    position: initial?.position || "",
    contact: initial?.contact || "",
    email: initial?.email || "",
    password: "",
    role: "user",
    track_ids: initial?.track_ids || [],
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleTrack = (id) => setForm((f) => ({
    ...f,
    track_ids: f.track_ids.includes(id) ? f.track_ids.filter((t) => t !== id) : [...f.track_ids, id],
  }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const { name, department, position, contact, track_ids } = form;
        await api.put(`/admin/users/${initial.id}`, { name, department, position, contact, track_ids });
        toast.success(`${form.name} updated`);
      } else {
        await api.post("/admin/users", form);
        toast.success(`${form.name} created — welcome email with credentials sent to ${form.email}`);
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const input = "w-full border hairline bg-white px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--brand-primary)]";

  return (
    <form onSubmit={submit} className="border-2 border-[color:var(--brand-primary)] bg-white p-6 space-y-4" data-testid="user-form">
      <div className="flex items-center justify-between">
        <div className="label-eyebrow text-[var(--brand-primary)]">{editing ? `Edit ${initial.name}` : "Add new user"}</div>
        <button type="button" onClick={onClose} className="text-neutral-400 hover:text-black" data-testid="user-form-close"><X size={16} /></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {FIELDS.map((f) => (
          <label key={f.key} className="block">
            <span className="text-xs text-neutral-500">{f.label}{f.required && " *"}</span>
            <input className={input} value={form[f.key]} onChange={set(f.key)} required={f.required}
                   placeholder={f.placeholder} data-testid={`user-form-${f.key}`} />
          </label>
        ))}
        {!editing && (
          <>
            <label className="block">
              <span className="text-xs text-neutral-500">Email Address *</span>
              <input type="email" className={input} value={form.email} onChange={set("email")} required
                     placeholder="jane@company.com" data-testid="user-form-email" />
            </label>
            <label className="block">
              <span className="text-xs text-neutral-500">Password * (min 6 characters)</span>
              <input type="text" className={input} value={form.password} onChange={set("password")} required minLength={6}
                     placeholder="Set their sign-in password" data-testid="user-form-password" />
            </label>
            {isSuper && (
              <label className="block">
                <span className="text-xs text-neutral-500">Role *</span>
                <select className={input} value={form.role} onChange={set("role")} data-testid="user-form-role">
                  <option value="user">User (trainee)</option>
                  <option value="admin">Admin (workspace manager)</option>
                </select>
                {form.role === "admin" && (
                  <span className="text-[11px] text-neutral-500 block mt-1">
                    Admins can manage trainees, content and see the manager scorecard for this workspace.
                  </span>
                )}
              </label>
            )}
          </>
        )}
      </div>
      {tracks.length > 0 && form.role !== "admin" && (
        <div data-testid="user-form-tracks">
          <span className="text-xs text-neutral-500 block mb-2">Training tracks (position-based curriculum — leave empty for the default track)</span>
          <div className="flex flex-wrap gap-2">
            {tracks.map((t) => (
              <label key={t.id} className={`flex items-center gap-2 border px-3 py-1.5 text-xs cursor-pointer transition-colors ${
                form.track_ids.includes(t.id) ? "border-[color:var(--brand-primary)] text-[var(--brand-primary)] bg-[color:var(--brand-surface2)]" : "hairline text-neutral-600"
              }`} data-testid={`user-form-track-${t.id}`}>
                <input type="checkbox" className="accent-[var(--brand-primary)]" checked={form.track_ids.includes(t.id)} onChange={() => toggleTrack(t.id)} />
                {t.name}
              </label>
            ))}
          </div>
        </div>
      )}
      {!editing && (
        <p className="text-xs text-neutral-500">A branded welcome email with these credentials and a "Start Day 1" link will be sent automatically.</p>
      )}
      <div className="flex gap-2">
        <button type="submit" disabled={saving} className="btn-primary text-sm" data-testid="user-form-submit">
          {saving ? "Saving…" : editing ? "Save changes" : "Create user"}
        </button>
        <button type="button" onClick={onClose} className="btn-ghost text-sm" data-testid="user-form-cancel">Cancel</button>
      </div>
    </form>
  );
};
